import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { registrationApi } from '../../services/api/registration';
import './app-registration.css';

type RegistrationForm = Readonly<{
  firstName: string;
  email: string;
  consent: boolean;
  company: string;
}>;

type FormErrors = Partial<Record<'email' | 'consent' | 'form', string>>;

const initialForm: RegistrationForm = {
  firstName: '',
  email: '',
  consent: false,
  company: '',
};

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const AccessFeature: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <div className="registration-feature">
    <span className="registration-feature-number">{number}</span>
    <div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
);

const MailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 5.75h18v12.5H3V5.75Z" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

const ArrowIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M14 7l5 5-5 5" />
  </svg>
);

export const AppRegistrationPage: React.FC = () => {
  const firstNameId = useId();
  const emailId = useId();
  const consentId = useId();
  const [form, setForm] = useState<RegistrationForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const updateForm = <Key extends keyof RegistrationForm>(
    key: Key,
    value: RegistrationForm[Key]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (key === 'email' || key === 'consent') {
      setErrors((current) => ({ ...current, [key]: undefined, form: undefined }));
    } else {
      setErrors((current) => ({ ...current, form: undefined }));
    }
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!isValidEmail(form.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!form.consent) {
      nextErrors.consent = 'Please accept the privacy and early-access email terms.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    const result = await registrationApi.register({
      firstName: form.firstName,
      email: form.email,
      consent: form.consent,
      company: form.company,
    });

    if (result.success) {
      setRegisteredEmail(result.data.email_hint);
    } else {
      setErrors({
        email: result.error.fieldErrors?.email,
        consent: result.error.fieldErrors?.consent,
        form: result.error.message,
      });
    }

    setIsSubmitting(false);
  };

  const resetRegistration = () => {
    setForm(initialForm);
    setErrors({});
    setRegisteredEmail('');
  };

  return (
    <div className="app-registration-page">
      <div className="registration-grid" aria-hidden="true" />
      <div className="registration-glow registration-glow-gold" aria-hidden="true" />
      <div className="registration-glow registration-glow-green" aria-hidden="true" />

      <header className="registration-header">
        <Link to="/" className="registration-logo" aria-label="Master Polyglots home">
          Master <span>Polyglots</span>
        </Link>
        <div className="registration-header-status">
          <span aria-hidden="true" />
          Mobile app early access
        </div>
      </header>

      <main className="registration-main">
        <section className="registration-story" aria-labelledby="registration-heading">
          <div className="registration-eyebrow">
            <span>Private launch list</span>
            <span>Release 01</span>
          </div>

          <h1 id="registration-heading">
            Master the craft.
            <br />
            <span>Build the future. 🚀</span>
          </h1>

          <p className="registration-lead">
            Join the first wave of Master Polyglots mobile learners. Get launch
            access, product updates, and a private code for our Intro to Software
            video.
          </p>

          <div className="registration-features" aria-label="Early access benefits">
            <AccessFeature
              number="01"
              title="Your private access pass"
              description="A one-time code delivered directly to your inbox."
            />
            <AccessFeature
              number="02"
              title="Intro to Software"
              description="Download the foundation lesson and start before launch."
            />
            <AccessFeature
              number="03"
              title="First through the door"
              description="Be notified when the Master Polyglots app is ready."
            />
          </div>

          <div className="registration-terminal" aria-hidden="true">
            <div className="registration-terminal-bar">
              <span>master-polyglots/access-pass</span>
              <div>
                <i />
                <i />
                <i />
              </div>
            </div>
            <div className="registration-terminal-body">
              <span className="terminal-prompt">$</span>
              <span>request --early-access</span>
              <span className="terminal-cursor" />
            </div>
            <div className="registration-terminal-output">
              <span>STATUS</span>
              <strong>READY TO BUILD</strong>
            </div>
          </div>
        </section>

        <section className="registration-card" aria-label="Early access signup">
          {registeredEmail ? (
            <div className="registration-success" role="status" aria-live="polite">
              <div className="registration-success-icon">
                <MailIcon />
              </div>
              <p className="registration-card-kicker">Transmission complete</p>
              <h2>Check your inbox.</h2>
              <p className="registration-success-copy">
                Your early-access pass is headed to <strong>{registeredEmail}</strong>.
              </p>

              <div className="registration-success-package">
                <span>Inside your email</span>
                <ul>
                  <li>Early-access confirmation</li>
                  <li>One-time download code</li>
                  <li>Intro to Software access</li>
                </ul>
              </div>

              <p className="registration-success-note">
                Delivery can take a few minutes. Check your promotions or spam folder
                if it does not arrive.
              </p>

              <Link to="/" className="registration-primary-link">
                Return to Master Polyglots
                <ArrowIcon />
              </Link>
              <button
                type="button"
                className="registration-text-button"
                onClick={resetRegistration}
              >
                Register a different email
              </button>
            </div>
          ) : (
            <>
              <div className="registration-card-heading">
                <p className="registration-card-kicker">Your invitation starts here</p>
                <h2>Claim early access.</h2>
                <p>
                  One email now. Your private pass and first lesson next.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="registration-field">
                  <label htmlFor={firstNameId}>
                    First name <span>Optional</span>
                  </label>
                  <input
                    id={firstNameId}
                    type="text"
                    name="first_name"
                    value={form.firstName}
                    onChange={(event) => updateForm('firstName', event.target.value)}
                    placeholder="What should we call you?"
                    autoComplete="given-name"
                    maxLength={80}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="registration-field">
                  <label htmlFor={emailId}>Email address</label>
                  <input
                    id={emailId}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={(event) => updateForm('email', event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? `${emailId}-error` : undefined}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.email && (
                    <p id={`${emailId}-error`} className="registration-error">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="registration-honeypot" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={(event) => updateForm('company', event.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="registration-consent">
                  <input
                    id={consentId}
                    type="checkbox"
                    checked={form.consent}
                    onChange={(event) => updateForm('consent', event.target.checked)}
                    aria-invalid={Boolean(errors.consent)}
                    aria-describedby={errors.consent ? `${consentId}-error` : undefined}
                    disabled={isSubmitting}
                  />
                  <label htmlFor={consentId}>
                    I agree to the{' '}
                    <Link to="/privacy">Privacy Policy</Link> and want early-access
                    emails from Master Polyglots.
                  </label>
                </div>
                {errors.consent && (
                  <p id={`${consentId}-error`} className="registration-error">
                    {errors.consent}
                  </p>
                )}

                {errors.form && (
                  <div className="registration-form-error" role="alert">
                    {errors.form}
                  </div>
                )}

                <button
                  type="submit"
                  className="registration-submit"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? 'Securing your place...' : 'Join the first wave'}</span>
                  {isSubmitting ? <i className="registration-spinner" /> : <ArrowIcon />}
                </button>

                <p className="registration-form-note">
                  No password. No payment. Unsubscribe whenever you want.
                </p>
              </form>
            </>
          )}
        </section>
      </main>

      <footer className="registration-footer">
        <span>&copy; {new Date().getFullYear()} Master Polyglots</span>
        <nav aria-label="Legal">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </footer>
    </div>
  );
};

export default AppRegistrationPage;
